import { useState, useRef, useEffect } from 'react';
import { Card, KpiCard, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { useAuthStore } from '@/stores/auth';
import styles from './Reports.module.css';

interface MonthlyReport {
  month: string;
  deals: number;
  revenue: number;
}

interface Customer {
  id: string;
  status: string;
  source: string;
}

interface Summary {
  deals_this_month?: number;
  total_commission?: number;
  avg_days?: number;
  top_portal?: string;
  top_portal_cvr?: number;
  prev_avg_days?: number;
  prev_commission?: number;
  target_deals?: number;
}

const stageLabel: Record<string, string> = {
  new: '新規',
  interested: '連絡中',
  viewing: '内見',
  applied: '申込',
  contracted: '成約',
};

const stageColors: Record<string, string> = {
  new: '#6366f1',
  interested: '#8b5cf6',
  viewing: '#3b82f6',
  applied: '#f59e0b',
  contracted: '#10b981',
};

const stageKeys = ['new', 'interested', 'viewing', 'applied', 'contracted'];

export function Reports() {
  const { data: monthly, loading: mLoading } = useApi<MonthlyReport[]>('/tenant/reports/monthly');
  const { data: customers } = useApi<Customer[]>('/tenant/customers');
  const { data: summary, refetch } = useApi<Summary>('/tenant/reports/summary');
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const targetRef = useRef<HTMLInputElement>(null);

  const dealsThisMonth = summary?.deals_this_month ?? 0;
  const totalCommission = summary?.total_commission ?? 0;
  const avgDays = summary?.avg_days;
  const topPortal = summary?.top_portal ?? '—';
  const prevAvgDays = summary?.prev_avg_days;
  const prevCommission = summary?.prev_commission ?? 0;
  const targetDeals = summary?.target_deals ?? 10;
  const topCvr = summary?.top_portal_cvr;

  useEffect(() => {
    if (editingTarget && targetRef.current) { targetRef.current.focus(); targetRef.current.select(); }
  }, [editingTarget]);

  const handleTargetSave = async () => {
    const val = Number(targetInput);
    if (!val || val < 1 || val > 9999) { toast('1〜9999の範囲で入力してください', 'error'); return; }
    const { token, user } = useAuthStore.getState();
    const base = import.meta.env.VITE_API_URL || '/api';
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (user?.tenantSlug) headers['X-Tenant-Slug'] = user.tenantSlug;
    try {
      const res = await fetch(`${base}/tenant/settings/target`, {
        method: 'PATCH', headers, body: JSON.stringify({ monthly_deal_target: val })
      });
      if (!res.ok) throw new Error();
      toast(`目標を${val}件に設定しました`, 'success');
      setEditingTarget(false);
      refetch();
    } catch { toast('目標の保存に失敗しました', 'error'); }
  };

  if (mLoading) return <div className={styles.page}>読み込み中...</div>;

  const commissionDelta = prevCommission > 0
    ? Math.round(((totalCommission - prevCommission) / prevCommission) * 100)
    : 0;

  // Monthly chart
  const maxDeals = Math.max(...(monthly ?? []).map((m) => m.deals), 1);

  // Pipeline from customers
  const stageCounts: Record<string, number> = {};
  (customers ?? []).forEach((c) => {
    const status = c.status;
    if (stageKeys.includes(status)) {
      stageCounts[status] = (stageCounts[status] || 0) + 1;
    }
  });
  const maxStage = Math.max(...stageKeys.map((k) => stageCounts[k] || 0), 1);

  // Total customers for percentage
  const totalInPipeline = stageKeys.reduce((sum, k) => sum + (stageCounts[k] || 0), 0);

  return (
    <div className={styles.page}>
      {/* KPI cards */}
      <div className={styles.kpiRow}>
        <div className={styles.kpiCustom}>
          <div className={styles.kpiLabel}>今月成約数</div>
          <div className={styles.kpiValue}>{dealsThisMonth}</div>
          <div className={styles.kpiDelta}>
            {editingTarget ? (
              <span className={styles.targetEdit}>
                目標 <input ref={targetRef} type="number" min={1} max={9999} value={targetInput}
                  onChange={e => setTargetInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleTargetSave(); if (e.key === 'Escape') setEditingTarget(false); }}
                  className={styles.targetInput} />件
                <button className={styles.targetBtn} onClick={handleTargetSave}>✓</button>
                <button className={styles.targetBtnCancel} onClick={() => setEditingTarget(false)}>✕</button>
              </span>
            ) : (
              <span className={styles.targetDisplay} onClick={() => { setTargetInput(String(targetDeals)); setEditingTarget(true); }}
                title="クリックして目標を変更">
                ↑ 目標 {targetDeals}件
              </span>
            )}
          </div>
        </div>
        <KpiCard
          label="仲介手数料累計"
          value={`¥${totalCommission.toLocaleString()}`}
          delta={commissionDelta > 0 ? `↑ +${commissionDelta}% vs 前月` : commissionDelta < 0 ? `↓ ${commissionDelta}% vs 前月` : ''}
          trend={commissionDelta > 0 ? 'up' : commissionDelta < 0 ? 'down' : 'flat'}
        />
        <KpiCard
          label="平均成約日数"
          value={avgDays != null ? `${avgDays}日` : '—'}
          delta={avgDays != null && prevAvgDays != null ? (avgDays < prevAvgDays ? `↑ 前月${prevAvgDays}日から改善` : avgDays > prevAvgDays ? `↓ 前月${prevAvgDays}日から悪化` : '') : ''}
          trend={avgDays != null && prevAvgDays != null ? (avgDays < prevAvgDays ? 'up' : avgDays > prevAvgDays ? 'down' : 'flat') : 'flat'}
        />
        <KpiCard
          label="媒体別CVR首位"
          value={topPortal}
          delta={topCvr != null ? `問合せ→成約 ${topCvr}%` : ''}
          trend={topCvr != null ? 'up' : 'flat'}
        />
      </div>

      <div className={styles.grid2}>
        {/* Monthly deals bar chart */}
        <Card title="月次成約実績">
          <div className={styles.chart}>
            {(monthly ?? []).map((m) => (
              <div key={m.month} className={styles.chartBar}>
                <span className={styles.chartLabel}>{m.month}</span>
                <div className={styles.chartTrack}>
                  <div
                    className={styles.chartFill}
                    style={{ width: `${(m.deals / maxDeals) * 100}%` }}
                  />
                </div>
                <span className={styles.chartValue}>{m.deals}件</span>
                <span className={styles.chartRevenue}>¥{m.revenue.toLocaleString()}</span>
              </div>
            ))}
            {(!monthly || monthly.length === 0) && (
              <div style={{ padding: 16, color: 'var(--ink3)', textAlign: 'center' }}>データなし</div>
            )}
          </div>
        </Card>

        {/* Stage pipeline */}
        <Card title="ステージ別パイプライン">
          <div className={styles.pipeline}>
            {stageKeys.map((key) => {
              const count = stageCounts[key] || 0;
              const pct = totalInPipeline > 0 ? Math.round((count / totalInPipeline) * 100) : 0;
              return (
                <div key={key} className={styles.pipelineRow}>
                  <span className={styles.pipelineLabel}>{stageLabel[key]}</span>
                  <div className={styles.pipelineTrack}>
                    <div
                      className={styles.pipelineFill}
                      style={{
                        width: `${(count / maxStage) * 100}%`,
                        background: stageColors[key] ?? 'var(--brand)',
                      }}
                    />
                  </div>
                  <span className={styles.pipelineCount}>{count}</span>
                  <span className={styles.pipelinePct}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
